import { useMemo, useState } from "react";
import moment from "moment";
import { convertToTitleCase } from "../../../utils/helpers";

const LABEL_OVERRIDES = {
  ip: "IP address",
  user_agent: "Browser / device",
};

function formatFieldLabel(label) {
  const normalized = String(label || "").trim();
  const key = normalized.toLowerCase().replace(/\s+/g, "_");
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  if (normalized.toLowerCase() === "ip") return LABEL_OVERRIDES.ip;
  return normalized;
}

function MetaField({ label, children, className = "" }) {
  return (
    <div className={`col-12 col-md-6 ${className}`.trim()}>
      <div
        className="small fw-semibold text-black2 text-uppercase mb-2"
        style={{ letterSpacing: "0.04em", fontSize: "11px" }}
      >
        {label}
      </div>
      <div className="text-black1" style={{ lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}

function DetailRow({ label, value, isLast }) {
  return (
    <div
      className={`row g-3 align-items-start mx-0 ${
        isLast ? "" : "border-bottom"
      }`.trim()}
      style={{
        borderColor: "#ececec",
        padding: "14px 20px",
      }}
    >
      <div className="col-12 col-sm-4 ps-0">
        <span className="small fw-semibold text-black2 d-block">
          {formatFieldLabel(label)}
        </span>
      </div>
      <div className="col-12 col-sm-8 ps-0 ps-sm-3">
        <span
          className="small text-black1 d-block"
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function DetailSection({ title, items = [] }) {
  if (!items.length) return null;

  return (
    <section className="mb-4">
      <div
        className="small fw-semibold text-black2 text-uppercase mb-3"
        style={{ letterSpacing: "0.04em", fontSize: "11px" }}
      >
        {title}
      </div>
      <div
        className="rounded-3 border bg-white overflow-hidden"
        style={{ borderColor: "#e0e0e0" }}
      >
        {items.map((item, index) => (
          <DetailRow
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value}
            isLast={index === items.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

export function getAuditRowSummary(row) {
  if (row?.summary) return row.summary;
  if (row?.action_label) return row.action_label;
  if (row?.action) return convertToTitleCase(row.action.replace(/\./g, " "));
  return "—";
}

export function buildFallbackDetailSections(row) {
  const metadata = row?.metadata;
  if (!metadata || typeof metadata !== "object") return [];

  const requestKeys = new Set(["ip", "user_agent", "_request"]);
  const eventItems = [];
  const requestItems = [];

  Object.entries(metadata).forEach(([key, value]) => {
    if (key === "_request" && value && typeof value === "object") {
      Object.entries(value).forEach(([reqKey, reqValue]) => {
        if (reqValue != null && reqValue !== "") {
          requestItems.push({
            label: reqKey,
            value: String(reqValue),
          });
        }
      });
      return;
    }

    if (requestKeys.has(key)) {
      if (value != null && value !== "") {
        requestItems.push({
          label: key,
          value: String(value),
        });
      }
      return;
    }

    if (value == null || value === "") return;

    eventItems.push({
      label: key,
      value: Array.isArray(value) ? value.join(", ") : String(value),
    });
  });

  const sections = [];
  if (eventItems.length) sections.push({ title: "Event details", items: eventItems });
  if (requestItems.length) sections.push({ title: "Request context", items: requestItems });
  return sections;
}

export function AuditEntryDetailModal({ row }) {
  const [showRaw, setShowRaw] = useState(false);

  const detailSections = useMemo(() => {
    if (Array.isArray(row?.detail_sections) && row.detail_sections.length) {
      return row.detail_sections;
    }
    return buildFallbackDetailSections(row);
  }, [row]);

  if (!row) return null;

  const actionLabel = row?.action_label || getAuditRowSummary(row);

  return (
    <div className="audit-entry-detail py-2">
      <div
        className="rounded-4 mb-4"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 49, 58, 0.06) 0%, rgba(49, 119, 140, 0.06) 100%)",
          border: "1px solid rgba(139, 49, 58, 0.12)",
          padding: "20px 24px",
        }}
      >
        <div
          className="text-muted text-uppercase fw-semibold mb-2"
          style={{ letterSpacing: "0.05em", fontSize: "11px" }}
        >
          Summary
        </div>
        <div
          className="fw-semibold text-black1 mb-0"
          style={{ fontSize: "17px", lineHeight: 1.5 }}
        >
          {getAuditRowSummary(row)}
        </div>
      </div>

      <div
        className="rounded-4 border mb-4"
        style={{
          borderColor: "#e0e0e0",
          backgroundColor: "#fafafa",
          padding: "20px 24px",
        }}
      >
        <div className="row g-4">
          <MetaField label="Event">
            <span className="fw-medium d-block">{actionLabel}</span>
            {row?.action && row.action !== actionLabel ? (
              <span className="small text-muted d-block mt-1">{row.action}</span>
            ) : null}
          </MetaField>

          <MetaField label="Date">
            {row?.created_at
              ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
              : "—"}
          </MetaField>

          <MetaField label="Actor">
            <span className="d-block">{row?.actor_label || row?.actor_type || "—"}</span>
            {row?.actor_type ? (
              <span className="small text-muted d-block mt-1">{row.actor_type}</span>
            ) : null}
          </MetaField>

          {row?.subject_label ? (
            <MetaField label="Subject">
              <span className="d-block">{row.subject_label}</span>
            </MetaField>
          ) : null}
        </div>
      </div>

      {detailSections.map((section) => (
        <DetailSection key={section.title} title={section.title} items={section.items} />
      ))}

      {detailSections.length === 0 ? (
        <p className="small text-muted mb-4 px-1">No additional details were recorded for this event.</p>
      ) : null}

      {row?.metadata && Object.keys(row.metadata).length > 0 ? (
        <div
          className="pt-4 mt-2"
          style={{ borderTop: "1px solid #ececec" }}
        >
          <button
            type="button"
            className="btn btn-sm btn-link px-0 py-1 text-decoration-none"
            style={{ color: "var(--color-secondary-readable)" }}
            onClick={() => setShowRaw((prev) => !prev)}
          >
            {showRaw ? "Hide raw metadata" : "View raw metadata"}
          </button>
          {showRaw ? (
            <pre
              className="small mb-0 text-black1 rounded-3 border mt-3"
              style={{
                backgroundColor: "#f7f7f7",
                borderColor: "#e0e0e0",
                padding: "16px 20px",
                maxHeight: "240px",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.55,
              }}
            >
              {JSON.stringify(row.metadata, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
